import React, { useMemo, useCallback, useContext, useState } from "react";
import { typography, borders, textColors } from "../../../design-system/tokens";
import { getElementTypeLabel } from "../../utils/internalElementIcons";
import { useAddElement } from "../../context/AddElementContext";
import { ELEMENT_ICONS } from "../addelements/elementIcons";
import { EditContext } from "../../context/EditContext";
import { apiFetch } from "../../../core/utils/api";
import {
  EditIcon,
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from "../../../design-system/icons";

// Per-component label overrides for the slot-manager UI. Most types use
// the canonical labels from ``getElementTypeLabel`` (``Title``, ``Text``,
// ``Image``…); we keep this map as an explicit extension point in case
// the slot-manager ever needs to disambiguate from other contexts.
//
// Historical note: ``button_collection`` used to render as "Call to
// Action" here because the original hero CTA slot was the only place
// users encountered button collections. The user pointed out that a
// BC isn't always a CTA — it's just a collection of buttons — so we now
// use the neutral "Button Collection" label everywhere. The map is left
// in place (rather than deleted in favour of ``getElementTypeLabel``
// directly) so a future per-context override can drop in here without a
// refactor.
const ITEM_TYPE_LABELS = {
  image: "Image",
  video: "Video",
  title: "Title",
  text: "Text",
  button: "Button",
  button_collection: "Button Collection",
  file: "File",
};

function typeLabel(type) {
  return ITEM_TYPE_LABELS[type] || getElementTypeLabel(type) || type;
}

function acceptsLabel(accepts) {
  if (!accepts || accepts.length === 0) return "Any";
  return accepts.map((t) => typeLabel(t)).join(" / ");
}

// Render the actual add-element icon for a given item type. Same icon system
// used by the "add element" UI, so users see consistent glyphs across the app.
function TypeIcon({ type, className = "w-3.5 h-3.5" }) {
  const Icon = ELEMENT_ICONS[type];
  if (!Icon) {
    return (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
      </svg>
    );
  }
  return <Icon className={className} />;
}

function TypeBadge({ type }) {
  return (
    <div
      className={`flex-shrink-0 w-6 h-6 rounded flex items-center justify-center
                 bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-200`}
    >
      <TypeIcon type={type} />
    </div>
  );
}

// Item-aware badge: identical footprint to ``TypeBadge`` but knows how to
// render a thumbnail preview when the item carries display-ready media
// (currently only Image, but the same pattern can extend to Video poster
// frames or File icons in the future). Falls back to the type icon when:
//
//   * The item is not an image (no URL → nothing to preview).
//   * The image item exists but has no URL set (user added an Image but
//     hasn't picked a file yet) — this preserves the "you can see what
//     type lives here" affordance from the empty state.
//   * The thumbnail fails to load (broken URL, signed URL expired, …) —
//     ``onError`` flips the local fallback latch so we don't keep
//     thrashing the network on every render.
//
// The hero serializer's ``_build_minimal_child_ref`` embeds
// ``image_url`` and ``cropped_image_url`` directly on slot-assignment
// refs (see ``portfolio_creator/website/serializers/core/items/hero.py``)
// so this component never needs to round-trip through ``/api/items/``.
function ItemBadge({ item }) {
  const [thumbErrored, setThumbErrored] = useState(false);

  const previewUrl = useMemo(() => {
    if (!item || item.type !== "image" || thumbErrored) return null;
    // Cropped takes precedence — it's already sized for thumbnail-ish
    // displays so it loads faster than the original. ``cropped_image_url``
    // is a backend property that returns ``None`` when no crop has been
    // configured, in which case we want to use the source image.
    return item.cropped_image_url || item.image_url || null;
  }, [item, thumbErrored]);

  if (previewUrl) {
    return (
      <div
        className="flex-shrink-0 w-6 h-6 rounded overflow-hidden bg-gray-100 dark:bg-dark-700"
      >
        <img
          src={previewUrl}
          alt={item?.alt_text || ""}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={() => setThumbErrored(true)}
        />
      </div>
    );
  }

  return <TypeBadge type={item?.type} />;
}

function SlotCard({
  slot,
  assignedItem,
  heroId,
  // Optional override label (used by MultiSlotCard to render a chip per
  // item: the row's header reads "Image 2 of 3" instead of the slot's
  // generic label).
  labelOverride,
  sublabelOverride,
  // Optional reorder controls — only supplied by MultiSlotCard for slots
  // with ``max_count > 1`` and only when a sibling exists in that
  // direction. Each handler is a no-arg function; presence of the
  // handler is what enables the corresponding chevron button.
  onMoveUp,
  onMoveDown,
  reordering = false,
}) {
  const { openAddModal } = useAddElement();
  const editContext = useContext(EditContext);
  const selectElement = editContext?.selectElement;
  const deleteElement = editContext?.deleteElement;
  const isEmpty = !assignedItem;

  const handleAddClick = useCallback(
    (e) => {
      e.stopPropagation();
      const firstType = slot.accepts?.[0];
      openAddModal(
        {
          heroId,
          order: 0,
          ...(firstType ? { restrictToType: firstType } : {}),
        },
        e,
      );
    },
    [openAddModal, heroId, slot.accepts],
  );

  // Build the element object used by selectElement / deleteElement.
  // Enriching with `hero: heroId` ensures EditContext's deleteElement invalidates
  // the hero query so the slot manager re-renders with the item removed.
  const assignedElement = useMemo(() => {
    if (!assignedItem) return null;
    return { ...assignedItem, hero: heroId };
  }, [assignedItem, heroId]);

  const handleSelect = useCallback(() => {
    if (!selectElement || !assignedElement) return;
    selectElement(assignedElement);
  }, [selectElement, assignedElement]);

  const handleCardClick = useCallback(
    (e) => {
      if (isEmpty) return;
      e.stopPropagation();
      handleSelect();
    },
    [isEmpty, handleSelect],
  );

  const handleCardKeyDown = useCallback(
    (e) => {
      if (isEmpty) return;
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        e.stopPropagation();
        handleSelect();
      }
    },
    [isEmpty, handleSelect],
  );

  const handleEditClick = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      handleSelect();
    },
    [handleSelect],
  );

  const handleDeleteClick = useCallback(
    async (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!deleteElement || !assignedElement) return;
      await deleteElement(assignedElement);
      // EditContext.deleteElement clears selection on success; re-select the
      // hero so the user remains in the same edit panel they were already on.
      if (selectElement && heroId != null) {
        selectElement({ id: heroId, type: "hero" });
      }
    },
    [deleteElement, assignedElement, selectElement, heroId],
  );

  // Use type-based label (e.g. "Title", "Text", "Button Collection") rather
  // than semantic slot labels (e.g. "Heading", "Body"). If the backend
  // already resolved a type-based label we use it directly; otherwise we
  // derive it from the first accepted type.
  const primaryType = slot.accepts?.[0];
  const slotDisplayLabel =
    labelOverride || slot.label || (primaryType ? typeLabel(primaryType) : "Slot");
  const accepts = acceptsLabel(slot.accepts);

  if (isEmpty) {
    return (
      <button
        type="button"
        onClick={handleAddClick}
        className={`w-full rounded-lg border-2 border-dashed border-gray-300/50 dark:border-gray-600/40
                   flex items-center gap-3 py-2.5 px-3
                   text-gray-400 dark:text-gray-500 hover:border-blue-400/60 hover:text-blue-500
                   dark:hover:border-blue-500/40 dark:hover:text-blue-400
                   transition-colors cursor-pointer bg-transparent`}
      >
        <div className="flex-shrink-0 w-6 h-6 rounded flex items-center justify-center bg-gray-100 dark:bg-dark-700">
          {primaryType ? (
            <TypeIcon type={primaryType} />
          ) : (
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          )}
        </div>
        <div className="flex flex-col items-start min-w-0 flex-1">
          <span className={`text-xs font-medium ${textColors.secondary}`}>{slotDisplayLabel}</span>
          <span className="text-[10px] leading-tight opacity-60">
            Empty &middot; accepts {accepts}
          </span>
        </div>
      </button>
    );
  }

  const itemLabel = assignedItem.label || assignedItem.name || getElementTypeLabel(assignedItem.type);
  const sublabel = sublabelOverride || itemLabel;

  const actionButtonClass = `flex-shrink-0 flex items-center justify-center w-6 h-6 rounded
                             text-gray-500 dark:text-dark-500
                             hover:text-gray-900 dark:hover:text-dark-100
                             hover:bg-gray-200/70 dark:hover:bg-dark-700
                             focus:outline-none focus:ring-1 focus:ring-gray-400 dark:focus:ring-gray-500
                             transition-colors`;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      aria-label={`Edit ${typeLabel(assignedItem.type)}: ${itemLabel}`}
      className={`group w-full rounded-lg border ${borders.default} bg-white/50 dark:bg-dark-800/50
                 flex items-center gap-3 py-2.5 px-3
                 cursor-pointer
                 hover:border-gray-400/70 dark:hover:border-gray-500/70
                 focus:outline-none focus:ring-2 focus:ring-blue-400/50
                 transition-colors`}
    >
      <ItemBadge item={assignedItem} />
      <div className="flex flex-col items-start min-w-0 flex-1">
        <span className={`text-xs font-medium ${textColors.primary} truncate w-full`}>
          {slotDisplayLabel}
        </span>
        <span className={`text-[10px] leading-tight ${textColors.muted} truncate w-full`}>
          {sublabel}
        </span>
      </div>
      <div className="flex-shrink-0 flex items-center gap-0.5">
        {/* Reorder controls render *before* edit/delete so the most
            destructive action (trash) stays last in the row — this matches
            the pattern users already know from list editors elsewhere in
            the app and avoids the "click trash by mistake while reaching
            for the down chevron" foot-gun. The buttons are only present
            when MultiSlotCard supplies a handler for that direction (i.e.
            top item gets no up arrow, bottom item gets no down arrow),
            which is also what gives screen readers a clear "first / last
            / middle" position cue without us having to spell it out. */}
        {(onMoveUp || onMoveDown) && (
          <>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (onMoveUp && !reordering) onMoveUp();
              }}
              disabled={!onMoveUp || reordering}
              aria-label={`Move ${typeLabel(assignedItem.type)} up`}
              title={onMoveUp ? "Move up" : "Already at the top"}
              className={`${actionButtonClass} ${
                !onMoveUp || reordering
                  ? "opacity-30 cursor-not-allowed"
                  : ""
              }`}
            >
              <ChevronUpIcon size="sm" />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (onMoveDown && !reordering) onMoveDown();
              }}
              disabled={!onMoveDown || reordering}
              aria-label={`Move ${typeLabel(assignedItem.type)} down`}
              title={onMoveDown ? "Move down" : "Already at the bottom"}
              className={`${actionButtonClass} ${
                !onMoveDown || reordering
                  ? "opacity-30 cursor-not-allowed"
                  : ""
              }`}
            >
              <ChevronDownIcon size="sm" />
            </button>
          </>
        )}
        <button
          type="button"
          onClick={handleEditClick}
          aria-label={`Edit ${typeLabel(assignedItem.type)}`}
          title={`Edit ${typeLabel(assignedItem.type)}`}
          className={actionButtonClass}
        >
          <EditIcon size="sm" />
        </button>
        <button
          type="button"
          onClick={handleDeleteClick}
          aria-label={`Delete ${typeLabel(assignedItem.type)}`}
          title={`Delete ${typeLabel(assignedItem.type)}`}
          className={actionButtonClass}
        >
          <TrashIcon size="sm" />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MultiSlotCard — renders slots with ``max_count > 1`` (currently just the
// Gallery slot). Shows one ``SlotCard`` per assigned item, plus a single
// "Add another" row when the slot isn't full. Every chip/card reuses
// ``SlotCard`` so the edit + delete affordances stay identical to the
// single-item path — the only UX delta is that you see multiple rows
// instead of one. Empty (no items assigned yet) falls back to the same
// dashed "Add X" CTA the single path uses, so the two shapes are visually
// continuous for the common "just added my first item" moment.
// ---------------------------------------------------------------------------

function MultiSlotCard({ slot, assignedItems, heroId }) {
  const { openAddModal } = useAddElement();
  const editContext = useContext(EditContext);
  const refreshElement = editContext?.refreshElement;
  const items = Array.isArray(assignedItems) ? assignedItems : [];
  const count = items.length;
  const maxCount = slot.max_count || Infinity;
  const canAddMore = count < maxCount;

  const [reordering, setReordering] = useState(false);
  const [reorderError, setReorderError] = useState(null);

  const handleAddMore = useCallback(
    (e) => {
      e.stopPropagation();
      const firstType = slot.accepts?.[0];
      openAddModal(
        {
          heroId,
          order: count,
          ...(firstType ? { restrictToType: firstType } : {}),
        },
        e,
      );
    },
    [openAddModal, heroId, slot.accepts, count],
  );

  // Reordering walks through the API rather than mutating order locally
  // because the slot's order field is the SAME field the renderer reads —
  // we want the server to be the source of truth, then refetch the hero
  // so every consumer (slot manager, renderer, internal-elements list) sees
  // the new sequence in lockstep. Optimistic UI would diverge from the
  // renderer until the next GET, which on hero-mode pages produces the
  // visible glitch of "the slot manager rearranged but the preview didn't".
  const handleSwap = useCallback(
    async (fromIndex, toIndex) => {
      if (
        !heroId ||
        reordering ||
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= count ||
        toIndex >= count ||
        fromIndex === toIndex
      ) {
        return;
      }

      const reordered = [...items];
      const [moved] = reordered.splice(fromIndex, 1);
      reordered.splice(toIndex, 0, moved);

      const payload = {
        slot_name: slot.name,
        item_ids: reordered
          .filter((item) => item && item.id != null && item.type)
          .map((item) => ({ id: item.id, type: item.type })),
      };
      if (payload.item_ids.length !== reordered.length) {
        // Refuse to call the API with a partial sequence — the backend
        // requires a full permutation and would respond 400 anyway.
        return;
      }

      setReordering(true);
      setReorderError(null);
      try {
        const response = await apiFetch(
          `/api/items/hero/${heroId}/reorder-slot/`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
            credentials: "include",
          },
        );
        if (!response.ok) {
          const text = await response.text();
          throw new Error(text || `HTTP ${response.status}`);
        }
        await response.json().catch(() => null);
        if (refreshElement) {
          await refreshElement(heroId, "hero");
        }
      } catch (err) {
        console.error("[HeroSlotManagerField] Reorder failed:", err);
        setReorderError(err?.message || "Failed to reorder items");
      } finally {
        setReordering(false);
      }
    },
    [heroId, items, count, slot.name, refreshElement, reordering],
  );

  // Empty state reuses the single-item empty SlotCard so the first-time
  // UX matches the one users already know. The multi-row layout only
  // kicks in after the first assignment.
  if (count === 0) {
    return <SlotCard slot={slot} assignedItem={null} heroId={heroId} />;
  }

  const primaryType = slot.accepts?.[0];
  const baseTypeLabel = primaryType ? typeLabel(primaryType) : "Item";

  return (
    <div className="space-y-1.5">
      {items.map((item, index) => {
        // Capacity-aware label ("Image 1 of 3") when max_count is finite.
        // Without the total it's "Image 1" — which is fine for the
        // theoretical unbounded case but less informative.
        const positionLabel = Number.isFinite(maxCount)
          ? `${baseTypeLabel} ${index + 1} of ${maxCount}`
          : `${baseTypeLabel} ${index + 1}`;
        // Only expose reorder handlers when there's somewhere to move to
        // AND there's more than one item in the slot (a single-item slot
        // shows no chevrons even though it visually fits the multi
        // layout — moving an item with no siblings is a no-op).
        const canReorder = count > 1;
        const onMoveUp =
          canReorder && index > 0 ? () => handleSwap(index, index - 1) : undefined;
        const onMoveDown =
          canReorder && index < count - 1
            ? () => handleSwap(index, index + 1)
            : undefined;
        return (
          <SlotCard
            key={`${slot.name}-${item?.type}-${item?.id}-${index}`}
            slot={slot}
            assignedItem={item}
            heroId={heroId}
            labelOverride={positionLabel}
            sublabelOverride={
              item?.label || item?.name || getElementTypeLabel(item?.type)
            }
            onMoveUp={onMoveUp}
            onMoveDown={onMoveDown}
            reordering={reordering}
          />
        );
      })}
      {reorderError && (
        <div className="px-3 text-[10px] text-rose-500" role="alert">
          {reorderError}
        </div>
      )}
      {canAddMore && (
        <button
          type="button"
          onClick={handleAddMore}
          className={`w-full rounded-lg border border-dashed border-gray-300/50 dark:border-gray-600/40
                     flex items-center gap-2 py-2 px-3
                     text-[11px] font-medium
                     text-gray-500 dark:text-gray-400
                     hover:border-blue-400/60 hover:text-blue-500
                     dark:hover:border-blue-500/40 dark:hover:text-blue-400
                     transition-colors cursor-pointer bg-transparent`}
          title={`Add another ${baseTypeLabel.toLowerCase()} (up to ${maxCount})`}
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span>
            Add another {baseTypeLabel.toLowerCase()}
            {Number.isFinite(maxCount) ? ` (${count}/${maxCount})` : ""}
          </span>
        </button>
      )}
    </div>
  );
}

function UnassignedItemCard({
  item,
  eligibleSlots,
  onPromote,
  promoting,
}) {
  const canPromote = !!eligibleSlots?.length && !promoting;

  const handlePromote = useCallback(
    (slotName) => (e) => {
      e.stopPropagation();
      if (onPromote) onPromote(item, slotName);
    },
    [item, onPromote],
  );

  // Display only the type label here. The user-facing "label" we used to
  // append (``item.label || item.name || getElementTypeLabel(item.type)``)
  // is, for the common case of an Image, the seeded ``alt_text`` from the
  // template preset — every preset hard-codes this to ``"Default image"``
  // (see ``portfolio_creator/website/models/templates/presets/.../*.py``),
  // so the row was reading ``Image: Default image`` for every untouched
  // image, which is noise rather than information. The status sublabel
  // below already explains what to do with the row ("Hidden — promote to
  // use" or "Not supported in this mode"), so the type alone is enough
  // to identify the item.
  return (
    <div
      className={`w-full rounded-lg border border-dashed ${borders.default}
                 flex items-center gap-3 py-2 px-3`}
    >
      <ItemBadge item={item} />
      <div className="flex flex-col items-start min-w-0 flex-1">
        <span className={`text-[11px] font-medium ${textColors.secondary} truncate w-full`}>
          {typeLabel(item.type)}
        </span>
        <span className={`text-[9px] ${textColors.muted}`}>
          {eligibleSlots?.length
            ? "Hidden — promote to use"
            : "Not supported in this mode"}
        </span>
      </div>

      {canPromote && (
        <div className="flex-shrink-0 flex items-center gap-1">
          {eligibleSlots.map((slot) => (
            <button
              key={slot.name}
              type="button"
              onClick={handlePromote(slot.name)}
              title={`Swap into ${slot.label || typeLabel(item.type)}`}
              className={`flex items-center gap-1 rounded px-1.5 py-1
                         text-[10px] font-medium
                         border ${borders.default}
                         bg-white/70 dark:bg-dark-800/70
                         text-gray-600 dark:text-gray-300
                         hover:border-blue-400/60 hover:text-blue-500
                         dark:hover:border-blue-500/40 dark:hover:text-blue-400
                         transition-colors`}
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4" />
              </svg>
              <span className="truncate max-w-[60px]">
                {slot.label || typeLabel(item.type)}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function HeroSlotManagerField({ allValues }) {
  const modeDef = allValues?.active_mode_definition;
  const slotAssignments = allValues?.slot_assignments;
  const unassignedItems = allValues?.unassigned_items;
  const heroId = allValues?.id;
  const internalElements = allValues?.internal_elements;

  const editContext = useContext(EditContext);
  const refreshElement = editContext?.refreshElement;

  const [promotingKey, setPromotingKey] = useState(null);
  const [promoteError, setPromoteError] = useState(null);

  const enrichedSlots = useMemo(() => {
    if (!modeDef?.slots || !slotAssignments) return [];

    // Enrich one assigned item with its ``internal_elements`` entry so
    // the card can show the user-facing label (e.g. the first words of
    // a Text, the alt text on an Image). The hero serializer returns
    // minimal refs in slot_assignments; internal_elements is where the
    // labels live.
    const enrichOne = (assigned) => {
      if (!assigned) return null;
      if (!internalElements) return assigned;
      const match = internalElements.find(
        (el) => el.id === assigned.id && el.type === assigned.type,
      );
      return match ? { ...assigned, ...match } : assigned;
    };

    return modeDef.slots.map((slot) => {
      const assigned = slotAssignments[slot.name];
      const isMulti = (slot.max_count || 1) > 1;
      if (isMulti) {
        const list = Array.isArray(assigned) ? assigned : [];
        return {
          slot,
          assignedItems: list.map(enrichOne).filter(Boolean),
          assignedItem: null,
          isMulti: true,
        };
      }
      return {
        slot,
        assignedItem: enrichOne(assigned),
        assignedItems: null,
        isMulti: false,
      };
    });
  }, [modeDef, slotAssignments, internalElements]);

  const enrichedUnassigned = useMemo(() => {
    if (!unassignedItems?.length) return [];
    return unassignedItems.map((item) => {
      if (internalElements) {
        const match = internalElements.find(
          (el) => el.id === item.id && el.type === item.type,
        );
        if (match) return { ...item, ...match };
      }
      return item;
    });
  }, [unassignedItems, internalElements]);

  const handlePromote = useCallback(
    async (item, slotName) => {
      if (!heroId) return;
      const key = `${item.type}-${item.id}`;
      setPromotingKey(key);
      setPromoteError(null);
      try {
        const response = await apiFetch(
          `/api/items/hero/${heroId}/assign-slot/`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              slot_name: slotName,
              item_id: item.id,
              item_type: item.type,
            }),
            credentials: "include",
          },
        );
        if (!response.ok) {
          const text = await response.text();
          throw new Error(text || `HTTP ${response.status}`);
        }
        await response.json().catch(() => null);
        if (refreshElement) {
          await refreshElement(heroId, "hero");
        }
      } catch (err) {
        console.error("[HeroSlotManagerField] Promote failed:", err);
        setPromoteError(err?.message || "Failed to update slot");
      } finally {
        setPromotingKey(null);
      }
    },
    [heroId, refreshElement],
  );

  if (!modeDef || !slotAssignments) return null;

  const capacity = modeDef?.item_capacity;

  const getEligibleSlotsForType = (type) => {
    if (!modeDef?.slots) return [];
    return modeDef.slots
      .filter((s) => Array.isArray(s.accepts) && s.accepts.includes(type))
      .map((s) => ({
        name: s.name,
        label: s.label || typeLabel(s.accepts?.[0]),
      }));
  };

  return (
    <div className="mt-4">
      <div className="mb-2 flex items-center justify-between">
        <label className={`${typography.label} ${textColors.secondary}`}>
          Slot Assignments
        </label>
        {capacity && (
          <span className={`text-[10px] ${textColors.muted}`}>
            {Object.entries(capacity)
              .filter(([, count]) => count > 0)
              .map(([type, count]) => `${count} ${typeLabel(type)}`)
              .join(", ")}
          </span>
        )}
      </div>

      <div className="space-y-1.5">
        {enrichedSlots.map(({ slot, assignedItem, assignedItems, isMulti }) =>
          isMulti ? (
            <MultiSlotCard
              key={slot.name}
              slot={slot}
              assignedItems={assignedItems}
              heroId={heroId}
            />
          ) : (
            <SlotCard
              key={slot.name}
              slot={slot}
              assignedItem={assignedItem}
              heroId={heroId}
            />
          ),
        )}
      </div>

      {enrichedUnassigned.length > 0 && (
        <div className="mt-3">
          <label className={`block mb-1.5 ${typography.label} ${textColors.muted}`}>
            Hidden Items ({enrichedUnassigned.length})
          </label>
          <div className="space-y-1">
            {enrichedUnassigned.map((item) => {
              const key = `${item.type}-${item.id}`;
              return (
                <UnassignedItemCard
                  key={key}
                  item={item}
                  eligibleSlots={getEligibleSlotsForType(item.type)}
                  onPromote={handlePromote}
                  promoting={promotingKey === key}
                />
              );
            })}
          </div>
          {promoteError && (
            <div className="mt-1 text-[10px] text-rose-500">
              {promoteError}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
